/* eslint-disable no-console */
import {
  isBefore,
  differenceInMinutes,
  differenceInSeconds,
  parseISO,
  formatDistanceToNow,
  isValid,
} from 'date-fns';
import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchNotificationsCounts } from 'api/endpoints/notifications';
import { fetchUserMe } from 'api/endpoints/users';
import { useAuth } from 'contexts/auth';
import {
  ONE_SECOND_IN_MS,
  ONE_MINUTE_IN_MS,
  ONE_MINUTE_IN_SECONDS,
  TIMEOUT_MINIMUM_MINUTES_REMAINING_TO_START_SILENT_COUNTER,
  TIMEOUT_MINUTES_REMAINING_TO_SHOW_ALERT,
} from 'lib/constants';

import type { Dispatch, RootState } from 'store/store.types';

export type SessionTimeoutKind = 'idle' | 'absolute';

/**
 * What a session-timeout warning shows.
 *
 * Attributes:
 *   kind: Which timeout expires first. The idle timeout resets when the user
 *     stays connected; the absolute timeout does not.
 *   remainingTime: Time left before sign-out, as words from formatDistanceToNow.
 *   onAction: The warning's button. For an idle timeout it refreshes the
 *     session; for an absolute timeout it signs out now.
 */
export type SessionTimeoutWarning = {
  kind: SessionTimeoutKind;
  remainingTime: string;
  onAction: () => void;
};

type UseSessionTimeoutWarningOptions = {
  onWarn: (warning: SessionTimeoutWarning) => void;
  onClear: () => void;
};

const parseTimeout = (value: Date | string | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

/**
 * Tracks the idle and absolute session timeouts that the notifications poller
 * stores in Redux, and signs the user out when the first one expires.
 *
 * Inside the last few minutes it calls `onWarn` on every tick, once a minute
 * and then once a second in the final minute, so the caller can show or update
 * a countdown. It calls `onClear` when the warning should go away: after the
 * user refreshes an idle session, when the poller stores a timeout that is no
 * longer inside the last few minutes (the user stayed connected in another
 * tab), and just before the forced sign-out. It may call `onClear` when no
 * warning is showing, so `onClear` must do nothing in that case.
 */
export function useSessionTimeoutWarning({
  onWarn,
  onClear,
}: UseSessionTimeoutWarningOptions): void {
  const { signOut } = useAuth();
  const dispatch = useDispatch<Dispatch>();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isSigningOutRef = useRef<boolean>(false);

  const sessionIdleTimeoutAt = useSelector<
    RootState,
    Date | string | undefined
  >((state) => state.auth?.notificationsCounts?.sessionIdleTimeoutAt);
  const sessionAbsoluteTimeoutAt = useSelector<
    RootState,
    Date | string | undefined
  >((state) => state.auth?.notificationsCounts?.sessionAbsoluteTimeoutAt);

  // Use refs to store latest values to avoid dependency issues
  const signOutRef = useRef(signOut);
  const dispatchRef = useRef(dispatch);
  const onWarnRef = useRef(onWarn);
  const onClearRef = useRef(onClear);

  useEffect(() => {
    signOutRef.current = signOut;
    dispatchRef.current = dispatch;
    onWarnRef.current = onWarn;
    onClearRef.current = onClear;
  });

  useEffect(() => {
    const forceSignOut = (): void => {
      // Prevent multiple sign-out attempts
      if (isSigningOutRef.current) {
        return;
      }
      isSigningOutRef.current = true;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Call signOut which submits a form to /account/signout
      // The server will handle the redirect to the sign-in page
      signOutRef.current();
    };

    const refreshIdleTimeout = async (): Promise<void> => {
      await fetchUserMe();
      onClearRef.current();
      // Refetch notification counts and update Redux
      const { data } = await fetchNotificationsCounts({
        query: { ownProfiles: true },
      });
      dispatchRef.current.auth.setNotificationsCounts(data);
    };

    const onActionByKind: Record<SessionTimeoutKind, () => void> = {
      idle: refreshIdleTimeout,
      absolute: forceSignOut,
    };

    const startSessionTimer = (): void => {
      // Don't continue if we're already signing out
      if (isSigningOutRef.current) {
        return;
      }

      const idle = parseTimeout(sessionIdleTimeoutAt);
      const absolute = parseTimeout(sessionAbsoluteTimeoutAt);

      if (!idle && !absolute) {
        onClearRef.current();
        return;
      }

      let whichTimeoutExpiresFirst: SessionTimeoutKind;

      if (idle && absolute) {
        whichTimeoutExpiresFirst = isBefore(idle, absolute)
          ? 'idle'
          : 'absolute';
      } else {
        whichTimeoutExpiresFirst = idle ? 'idle' : 'absolute';
      }

      const timeoutDate = whichTimeoutExpiresFirst === 'idle' ? idle : absolute;
      if (!timeoutDate) return;

      const now = new Date();
      const remainingMinutes = differenceInMinutes(timeoutDate, now);
      const remainingSeconds = differenceInSeconds(timeoutDate, now);

      // The poller can move the timeout later while a warning is showing, for example after
      // the user stays connected in another tab. Take the old warning down, since no later
      // tick will update it.
      if (remainingMinutes >= TIMEOUT_MINUTES_REMAINING_TO_SHOW_ALERT) {
        onClearRef.current();
      }

      if (
        remainingMinutes >
        TIMEOUT_MINIMUM_MINUTES_REMAINING_TO_START_SILENT_COUNTER
      ) {
        return;
      }

      if (remainingSeconds <= 0) {
        if (import.meta.env.DEV) {
          console.log(
            '%c [TIMEOUT SESSION] SESSION EXPIRED!',
            'background: red; color: white',
          );
        }

        onClearRef.current();
        forceSignOut();
        return;
      }

      if (remainingMinutes < TIMEOUT_MINUTES_REMAINING_TO_SHOW_ALERT) {
        const remainingTime = formatDistanceToNow(timeoutDate, {
          includeSeconds: true,
        });

        onWarnRef.current({
          kind: whichTimeoutExpiresFirst,
          remainingTime,
          onAction: onActionByKind[whichTimeoutExpiresFirst],
        });

        if (import.meta.env.DEV) {
          const debuggerText = `%c [TIMEOUT SESSION] ${whichTimeoutExpiresFirst} Session will expire in ${remainingTime}`;
          console.log(debuggerText, 'background: red; color: white');
        }
      }

      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(
        startSessionTimer,
        remainingSeconds < ONE_MINUTE_IN_SECONDS
          ? ONE_SECOND_IN_MS
          : ONE_MINUTE_IN_MS,
      );
    };

    // Defer to next tick to avoid updating parent state during render
    const initialTimeoutId = setTimeout(startSessionTimer, 0);

    return () => {
      clearTimeout(initialTimeoutId);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [sessionIdleTimeoutAt, sessionAbsoluteTimeoutAt]);
}
